import React, { useState, useEffect, useRef } from 'react';
import { Shop } from '../services/shopService';

interface SearchSuggestion {
  id: string;
  title: string;
  subtitle: string;
  type: 'shop' | 'product' | 'category';
  icon: string;
  data?: any;
}

interface SmartSearchProps {
  onSearch: (query: string) => void;
  onSelectShop?: (shop: Shop) => void;
  shops: Shop[];
  placeholder?: string;
  className?: string;
}

const SmartSearch: React.FC<SmartSearchProps> = ({
  onSearch,
  onSelectShop,
  shops,
  placeholder = "Search shops, products...",
  className = ""
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Load search history from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('search_history');
      if (saved) {
        setSearchHistory(JSON.parse(saved));
      }
    } catch (error) {
      console.warn('Failed to load search history:', error);
    }
  }, []);

  // Generate suggestions based on query
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    const newSuggestions: SearchSuggestion[] = [];

    // Shop suggestions
    shops.forEach(shop => {
      if (shop.name.toLowerCase().includes(query.toLowerCase()) ||
          (shop.description && shop.description.toLowerCase().includes(query.toLowerCase())) ||
          shop.type.toLowerCase().includes(query.toLowerCase())) {
        newSuggestions.push({
          id: `shop-${shop.id}`,
          title: shop.name,
          subtitle: shop.description || 'No description available',
          type: 'shop',
          icon: getShopIcon(shop.type),
          data: shop
        });
      }
    });

    // Category suggestions
    const categories = ['grocery', 'medical', 'electronics', 'clothing', 'pharmacy', 'bakery', 'restaurant'];
    categories.forEach(category => {
      if (category.toLowerCase().includes(query.toLowerCase())) {
        newSuggestions.push({
          id: `category-${category}`,
          title: `${category.charAt(0).toUpperCase() + category.slice(1)} Shops`,
          subtitle: `Browse all ${category} shops`,
          type: 'category',
          icon: getShopIcon(category),
          data: category
        });
      }
    });

    // Product suggestions (mock data)
    const products = ['rice', 'flour', 'spices', 'medicines', 'electronics', 'clothes'];
    products.forEach(product => {
      if (product.toLowerCase().includes(query.toLowerCase())) {
        newSuggestions.push({
          id: `product-${product}`,
          title: product.charAt(0).toUpperCase() + product.slice(1),
          subtitle: `Search for ${product} products`,
          type: 'product',
          icon: '🛍️',
          data: product
        });
      }
    });

    setSuggestions(newSuggestions.slice(0, 8)); // Limit to 8 suggestions
  }, [query, shops]);

  const getShopIcon = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'medical': return '🏥';
      case 'grocery': return '🥬';
      case 'electronics': return '📱';
      case 'clothing': return '👕';
      case 'pharmacy': return '💊';
      case 'bakery': return '🥖';
      case 'restaurant': return '🍛';
      default: return '🏪';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(true);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSuggestionSelect = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.title);
    setShowSuggestions(false);
    setSelectedIndex(-1);

    // Add to search history
    const newHistory = [suggestion.title, ...searchHistory.filter(item => item !== suggestion.title)].slice(0, 10);
    setSearchHistory(newHistory);
    try {
      localStorage.setItem('search_history', JSON.stringify(newHistory));
    } catch (error) {
      console.warn('Failed to save search history:', error);
    }

    // Handle different suggestion types
    if (suggestion.type === 'shop' && onSelectShop) {
      onSelectShop(suggestion.data);
    } else {
      onSearch(suggestion.title);
    }
  };

  const handleSearch = () => {
    if (query.trim()) {
      // Add to search history
      const newHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 10);
      setSearchHistory(newHistory);
      try {
        localStorage.setItem('search_history', JSON.stringify(newHistory));
      } catch (error) {
        console.warn('Failed to save search history:', error);
      }

      onSearch(query);
      setShowSuggestions(false);
    }
  };

  const removeFromHistory = (item: string) => {
    const newHistory = searchHistory.filter(historyItem => historyItem !== item);
    setSearchHistory(newHistory);
    try {
      localStorage.setItem('search_history', JSON.stringify(newHistory));
    } catch (error) {
      console.warn('Failed to update search history:', error);
    }
  };

  const handleHistoryClick = (item: string) => {
    setQuery(item);
    onSearch(item);
    setShowSuggestions(false);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`smart-search-container ${className}`}>
      <div className="search-container">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          className="search-input"
        />
        <button 
          className="search-btn"
          onClick={handleSearch}
          aria-label="Search"
        >
          🔍
        </button>
      </div>

      {showSuggestions && (
        <div ref={suggestionsRef} className="search-suggestions">
          {suggestions.length > 0 ? (
            <>
              {suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.id}
                  className={`search-suggestion ${index === selectedIndex ? 'active' : ''}`}
                  onClick={() => handleSuggestionSelect(suggestion)}
                >
                  <span className="icon">{suggestion.icon}</span>
                  <div className="content">
                    <div className="title">{suggestion.title}</div>
                    <div className="subtitle">{suggestion.subtitle}</div>
                  </div>
                  <span className="type">{suggestion.type}</span>
                </div>
              ))}
            </>
          ) : query.length >= 2 ? (
            <div className="search-no-results">
              <div className="icon">🔍</div>
              <div>No results found for "{query}"</div>
            </div>
          ) : null}

          {searchHistory.length > 0 && suggestions.length === 0 && (
            <>
              <div className="search-recent">Recent Searches</div>
              {searchHistory.slice(0, 5).map((item, index) => (
                <div
                  key={index}
                  className="search-suggestion"
                  onClick={() => handleHistoryClick(item)}
                >
                  <span className="icon">🕒</span>
                  <div className="content">
                    <div className="title">{item}</div>
                    <div className="subtitle">Recent search</div>
                  </div>
                  <span 
                    className="remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromHistory(item);
                    }}
                  >
                    ✕
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartSearch;
